/**
 * LA PERSONNALITÉ DE LA CONSOLE — ce qu'elle peint autrement que le défaut de la charte.
 *
 * ⚠️ SORTI DE `App.tsx` (2026-09-05) pour que la PLANCHE DE CHARTE puisse le lire. Une planche qui
 * recopierait ces valeurs deviendrait une seconde charte, et dériverait de la vraie au premier
 * ajustement — c'est-à-dire exactement ce qu'elle est censée empêcher.
 */
import type { WebThemeOverrides } from '@orgazone/ui-tokens';

/**
 * Personnalité Console — portée de `design/Super Admin Orgazone v2`.
 * (Référence de module STABLE — pas un littéral dans le JSX, sinon recalcul à chaque rendu.)
 *
 * ⚠️ GRIS ARDOISE, et pas les gris partagés. Les défauts viennent de la maquette de l'EDI : des gris
 * NEUTRES, sans dominante. La Console utilise une famille ardoise, teintée bleu — c'est ce qui la rend
 * plus harmonieuse, ses gris partageant la dominante froide de l'accent turquoise là où un gris pur le
 * laisse seul de sa couleur (retour utilisateur 2026-08-11). Défini ICI et non dans les défauts
 * partagés : les imposer restylerait le store et l'EDI sans que personne l'ait demandé.
 */
export const CONSOLE_THEME: WebThemeOverrides = {
  neutrals: {
    light: {
      // ⚠️ Le 50 est TRANSLUCIDE (maquette), pas un gris plein. Il sert de fond aux surfaces posées SUR
      // une carte vitrée (tuiles de service, en-têtes de tableau, bandeaux) : opaque, il bouchait le
      // verre et ces surfaces devenaient des aplats — c'est ce qui donnait aux tuiles une autre couleur
      // que la maquette (constaté 2026-08-11).
      50: 'rgba(15,23,42,.03)', 100: 'rgba(15,23,42,.065)', 200: 'rgba(15,23,42,.11)', 300: '#cbd5e1',
      400: '#5f6d80', 500: '#64748b', 600: '#475569', 700: '#334155', 800: '#1e293b', 900: '#0f172a',
      paper: '#ffffff', canvas: '#f5f7fa',
    },
    dark: {
      50: 'rgba(148,163,184,.07)', 100: 'rgba(148,163,184,.11)', 200: 'rgba(148,163,184,.16)', 300: '#475569',
      400: '#94a3b8', 500: '#8695a8', 600: '#a9b7c6', 700: '#c7d2de', 800: '#dde5ee', 900: '#eff3f8',
      paper: '#131c27', canvas: '#0b1119',
    },
  },
  // ⚠️ L'ITEM ACTIF DE LA CONSOLE DIVERGE DU DÉFAUT, ET C'EST MESURÉ (2026-08-12) : la charte peint un
  // aplat de marque avec du blanc (§24.9sexies-ter g, Store/EDI/back-office) ; la maquette de la
  // Console peint une TEINTE à 14 % avec encre claire et anneau interne, ce qui donne 10,9:1 contre
  // 5,3:1. Les deux passent AA — la divergence tient à la mesure, pas au goût, et elle vit ICI plutôt
  // que dans une redéfinition locale de `.nav` (qui est exactement ce qu'on vient de supprimer).
  navActive: {
    bg: 'rgba(45,212,191,.14)', ink: '#5eead4', ring: 'inset 0 0 0 1px rgba(45,212,191,.24)',
  },
  // ⚠️ LE RAIL RESTE SOMBRE DANS LES DEUX MODES — c'est l'identité de la maquette, et `sidebarDark`
  // portait déjà cette inversion. Son texte (#9dadbf) est plus SOMBRE que l'ancien (#c4ccd2), mais il
  // se pose sur un fond bien plus sombre : le contraste AUGMENTE. Comparer deux encres sans leurs
  // fonds ne veut rien dire — erreur que j'avais commise dans le bilan avant de vérifier.
  sidebarDark: {
    bg: 'linear-gradient(180deg,#0a121b,#070b10 62%)', text: '#9dadbf', textDim: '#5f6d80',
    hover: 'rgba(148,163,184,.09)', line: 'rgba(148,163,184,.13)',
  },
  sidebarLight: {
    bg: 'linear-gradient(180deg,#0a121b,#070b10 62%)', text: '#9dadbf', textDim: '#5f6d80',
    hover: 'rgba(148,163,184,.09)', line: 'rgba(148,163,184,.13)',
  },
};
